import { FormGroup, Validators } from '@angular/forms';
import { FormValidatorMessages } from '../enums/forms.enums';

export class BaseFormComponent {

    public FormValidatorMessages = FormValidatorMessages;
    public isSubmitting = false;

    /**
     * Set true the instant a submit succeeds, before this leaf's own success/info modal is shown, and
     * cleared when the form is reset. The underlying FormGroup can still report `.dirty` in that window
     * (resetForm() hasn't run yet) — subclasses that don't already mark the form pristine synchronously
     * on success must AND this into their canDeactivate() dirty check, otherwise leaving via router
     * navigation while the success modal is still up would wrongly prompt "changes will be lost" over
     * data that has already been saved.
     */
    protected submitSucceeded = false;

    /** Resolver for an in-flight canDeactivate() promise, settled by the leave-confirmation modal's outputs. */
    private pendingLeaveResolve?: (canLeave: boolean) => void;

    /** The in-flight promise itself, so a re-entrant call can join it instead of being denied outright. */
    private pendingLeavePromise?: Promise<boolean>;

    public isFieldRequired(form: FormGroup, field: string): boolean {
        const control = form.get(field);
        return control?.hasValidator(Validators.required) || false;
    }

    /**
     * Shared CanComponentDeactivate entry point. When `isDirty` is true, invokes `showLeaveConfirmation`
     * (expected to show this leaf's own ConfirmationModalComponent) and returns a promise that resolves
     * once `resolvePendingLeave` is called from that modal's confirmed/cancelled handlers. Re-entrant
     * safe: a second call while one is already pending joins the same promise rather than being denied
     * immediately — otherwise a user who triggers a second navigation while the modal is still open would
     * have that attempt canceled outright, never honoring the choice they go on to make in the modal.
     */
    protected awaitLeaveConfirmation(isDirty: boolean, showLeaveConfirmation: () => void): boolean | Promise<boolean> {
        if (!isDirty) {
            return true;
        }
        if (this.pendingLeavePromise) {
            return this.pendingLeavePromise;
        }
        showLeaveConfirmation();
        this.pendingLeavePromise = new Promise<boolean>(resolve => { this.pendingLeaveResolve = resolve; });
        return this.pendingLeavePromise;
    }

    /** Settles a pending awaitLeaveConfirmation() promise. Returns true iff one was actually pending. */
    protected resolvePendingLeave(canLeave: boolean): boolean {
        if (!this.pendingLeaveResolve) {
            return false;
        }
        this.pendingLeaveResolve(canLeave);
        this.pendingLeaveResolve = undefined;
        this.pendingLeavePromise = undefined;
        return true;
    }
}